/** @file Finds the binding matching a key event in the active contexts and runs its handler. */
import { matchEvent } from '../matchEvent';
import type { KeySourceEvent } from '../keyTarget';
import type { Binding } from '../types';
import { activeContexts } from './activeContexts';
import { keyState } from './keyState';

/** No evaluator means a `when` binding stays off (fail closed). */
function isEnabled(binding: Binding): boolean {
  return binding.when === undefined || (keyState.when?.(binding.when) ?? false);
}

function findHandler(context: string, event: KeySourceEvent): (() => void) | undefined {
  for (const registration of [...keyState.registrations].reverse()) {
    if (registration.context !== context) continue;
    for (const binding of registration.table) {
      const handler = registration.handlers[binding.id];
      if (handler && isEnabled(binding) && matchEvent(binding, event, keyState.platform)) return handler;
    }
  }
  return undefined;
}

/** Most specific context first, newest registration first; calls preventDefault only when a binding matched. */
export function dispatchBinding(event: KeySourceEvent): void {
  for (const context of activeContexts()) {
    const handler = findHandler(context, event);
    if (!handler) continue;
    event.preventDefault();
    handler();
    return;
  }
}
